/**
 * Types used by fetchPost, fetchEmbeddedPost, and content enrichment.
 * ConfigProps, MenuItem, and related config types are defined in ../../configTypes.
 */
import type { MenuItem, ConfigProps } from '../../configTypes';
import type { TransformedSeo } from '../transform/transformSEO';

export interface ElementAttributes {
  [key: string]: string | ElementAttributes[] | undefined;
  children?: ElementAttributes[];
  textContent?: string;
  originalMarkup?: string;
  innerMarkup?: string;
}

export type TransformedItemPost = {
  id: string;
  title: string;
  link: string;
  image?: string;
  imagealt?: string;
  content: string;
  date: string;
  author?: string;
  brow?: string;
  contributors?: unknown[];
};

export type TransformedItemContributor = {
  id: string;
  name: string;
  title: string;
  link: string;
  image?: string;
  imagealt?: string;
};

export type SchemaTypeSeo = 'ItemList' | 'FAQPage' | 'HowTo' | 'Occupation';

export type SeoContentData = {
  [schemaName: string]: { [dataAttribute: string]: string[] };
};

export interface SeoTransformedContentData {
  faqPage?: { questions: Array<{ text: string; answer?: string }> };
  howToPage?: {
    title: string;
    description?: string;
    totalTime?: string;
    estimatedCost?: { currency?: string; value: string };
    steps: Array<{ text: string; answer?: string }>;
  };
  occupation?: {
    name: string;
    alternateName?: string;
    description?: string;
    occupationalCategory?: string;
    occupationLocationName?: string;
    educationRequirements?: string | string[];
    experienceRequirements?: string | string[];
    skills?: string | string[];
    estimatedSalary?: unknown;
    occupationalOutlook?: string;
  };
}

export interface EnrichedContent {
  rankings?: Record<string, unknown>;
  'sonic-hero'?: unknown;
  byline?: unknown;
  embeddedPosts?: Record<string, TransformedItemPost> | null;
  embeddedPostsByCategories?: Record<string, TransformedItemPost[]> | null;
  embeddedContributors?: Record<string, TransformedItemContributor> | null;
  gravityForms?: unknown;
  tocItems?: Array<{ title: string; href: string }>;
  seoTransformedContentData?: SeoTransformedContentData;
  seoSchemasToRender?: SchemaTypeSeo[];
}

export interface TocItem {
  id: string;
  name: string;
  class: string;
}

export enum SitemapEnvironment {
  LOCAL = 'local',
  DEV = 'dev',
  PROD = 'prod',
}
export type SitemapCMSEnvironment = `${SitemapEnvironment}`;

export interface SitemapApiItem {
  url: string;
  title: string;
  category: string;
  lastModified: string;
}

export interface SitemapArticleObject {
  uri: string;
  title: string;
  lastModified: string;
  category?: string | null;
}

export interface SitemapApiResponse {
  sitemap: SitemapApiItem[];
}

export interface GroupedSitemapResponse {
  [categoryName: string]: SitemapArticleObject[];
}

// ---------------------------------------------------------------------------
// fetchPostsByCategory
// ---------------------------------------------------------------------------

export type TransformedCategory = {
  categoryName: string;
  categorySlug?: string;
  categoryUri?: string;
  contentNodes: TransformedItemPost[];
};

export type PostsByCategoryResult = {
  generalSettings: Record<string, string>;
  headerMenuItems: { nodes: MenuItem[] };
  footerMenuItems: { nodes: MenuItem[] };
  auxiliaryMenuItems?: { nodes: MenuItem[] };
  posts: TransformedItemPost[];
  children: TransformedCategory[];
  parentSlug?: string;
  siblings?: TransformedCategory[];
  categoryName?: string;
  categoryDescription?: string;
  parentCategoryName?: string;
  transformedSEO: TransformedSeo;
};

// ---------------------------------------------------------------------------
// fetchPrivatePost
// ---------------------------------------------------------------------------

export type ContentNodeShape = {
  content?: string;
  contributors?: unknown;
  customDateType?: string;
  date?: string;
  additionalBylineDescription?: string;
  featuredImage?: unknown;
  seo?: { canonicalUrl?: string; openGraph?: { description?: string } };
  title?: string;
};

export interface PrivatePostNode {
  contentNode: Record<string, unknown>;
  generalSettings?: Record<string, string>;
  headerMenuItems?: { nodes: MenuItem[] };
  footerMenuItems?: { nodes: MenuItem[] };
  auxiliaryMenuItems?: { nodes: MenuItem[] };
  siteSettings?: ConfigProps;
  enrichedContent?: EnrichedContent | null;
  transformedSEO?: TransformedSeo;
}
